/* eslint-disable no-magic-numbers */
import {
  COMPARISON_OPTIONS,
  CalendarOptions,
  FIELD_TYPES,
  GROUPING_LEVEL,
  PERIOD_OPTIONS,
  STROKE_BORDER_POSITIONS,
  TABLE_FONT_WEIGHTS,
  TableStyle
} from './types';

// constant to track changes in widget interface that requires logic to upgrade from old interface
export const WIDGET_VERSION = '1.0.0';

export const FETCH_DATA_DELAY = 1000;
export const GET_FIN_DATA_DELAY_TIME = 4000;
export const SAVE_DELAY_TIME = 3000;

export const LAYOUT_STEP = 10;
export const WIDTH_STEPS = 96;

export const TABLE_FIRST_COLUMN_WIDTH = 160;
export const TABLE_COLUMN_WIDTH = 80;
export const TOP_BAR_HEIGHT = 58;
export const ONBOARDING_TOP_BAR_HEIGHT = 166;

// Workspaces where Futrli templates can be created and managed
export const FUTRLI_MANAGEMENT_WORKSPACES = [
  '248dab0a-6ed7-4d2a-9c59-03995bc8eedb', // staging futrli management workspace id
  '7426dabb-e87e-475d-adf8-ae6734e6b6f1' // prod futrli management workspace id
];

export const staticFormulaRefs = [
  'v2:static:gross_profit:transactional',
  'v2:static:gross_profit_%:ratio',
  'v2:static:operating_profit:transactional',
  'v2:static:operating_profit_%:ratio',
  'v2:static:net_profit:transactional',
  'v2:static:net_profit_%:ratio',
  'v2:static:total_assets:cumulative',
  'v2:static:net_assets:cumulative',
  'v2:static:total_liabilities:cumulative',
  'v2:static:cash_from_business_activities:cash_transactional',
  'v2:static:cash_from_financing_and_investing:cash_transactional',
  'v2:static:starting_balance:cash_cumulative',
  'v2:static:cash_in:cash_transactional',
  'v2:static:cash_out:cash_transactional',
  'v2:static:net_cash_flow:cash_transactional',
  'v2:static:closing_balance:cash_cumulative'
];

export const dummyLiveForecastId = 'fa79510f-bd46-5bd5-8fee-6fe23355d2f8';
export const dummyBudgetForecastId = 'f24c4f3d-9d43-590f-b1c2-0476b512eae2';
export const dummyScenarioForecastId = 'bf511083-c324-5bf7-bb52-44cc51e7dff5';

/**
 * The well-known dummy forecast ids used by report templates. Widgets referencing any of these are
 * reported to the backend as a single `dummy-data` referenced datasource rather than a real forecast.
 */
export const dummyForecastIds = [dummyLiveForecastId, dummyBudgetForecastId, dummyScenarioForecastId];

export const datasetComparisonRef = 'multi_dataset_comparison';
export const totalVarianceRef = 'v1:accrual:column_calculations:total';

export const v2ComparisonColumnRefs = {
  VARIANCE_VALUE: 'v1:accrual:dataset_comparison:variance_value',
  VARIANCE_PERCENT: 'v1:accrual:dataset_comparison:variance_percent'
};

// other data
export const DATE_FORMAT = 'yyyy-MM-dd';
export const MONTH_FORMAT = 'MMMM yyyy';
export const globalFinancialRefs = ['profit_and_loss', 'balance_sheet', 'cash_flow', 'drivers'];
export const actualForecastIds = ['actual-data', 'prior-year-actuals'];

// kind of the dataset reference added to a report's data reference mapping for a dataset that is no longer available
export const UNAVAILABLE_DATASET_KIND = 'unavailable';

// forecast id sent to the financial data endpoint for actuals-only datasets
export const actualsFinDataForecastId = 'actuals';

export const sizeModifier = 4 / 5;
export const pageSizesOptions = [
  {
    value: 'A4',
    label: 'A4 (8.27” x 11.69”)',
    dimensions: { width: 1240 * sizeModifier, height: 1754 * sizeModifier }
  },
  { value: 'A5', label: 'A5 (5.78” x 8.28”)', dimensions: { width: 877 * sizeModifier, height: 1240 * sizeModifier } },
  { value: 'A6', label: 'A6 (4.13” x 5.83”)', dimensions: { width: 620 * sizeModifier, height: 877 * sizeModifier } }
];

export const DEFAULT_PERIOD_NUMBER = 3;

export const defaultCalendarOptions: CalendarOptions = {
  type: 'monthly',
  startDate: PERIOD_OPTIONS.THIS_PERIOD,
  comparision: COMPARISON_OPTIONS.PREVIOUS,
  periods: DEFAULT_PERIOD_NUMBER
};

export const ClassifierLevel = {
  '': GROUPING_LEVEL.CATEGORIES,
  'static': GROUPING_LEVEL.CATEGORIES,
  'group': GROUPING_LEVEL.GROUPS,
  'elimination-group': GROUPING_LEVEL.GROUPS,
  'group-by-account-name': GROUPING_LEVEL.GROUPS,
  'group-by-org-group-name': GROUPING_LEVEL.GROUPS,
  'account': GROUPING_LEVEL.ACCOUNTS,
  'eliminated-account': GROUPING_LEVEL.ACCOUNTS,
  'driver-group': GROUPING_LEVEL.DRIVER_GROUPS,
  'row-formula': GROUPING_LEVEL.ASSUMPTIONS,
  'template-formula': GROUPING_LEVEL.ASSUMPTIONS,
  'freestyle-driver': GROUPING_LEVEL.ASSUMPTIONS,
  'formula-driver': GROUPING_LEVEL.ASSUMPTIONS,
  'assumption': GROUPING_LEVEL.ASSUMPTIONS
};

export const textWidgetAllowedTags = [
  'p',
  'strong',
  'em',
  'u',
  'span',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'br',
  'ul',
  'li',
  'ol',
  'img'
];

export const varianceBG = '#F2F5F6';
export const defaultFillColor = '#ffffff';
export const defaultColor = '#000000';

export const fieldTypeColors: Record<FIELD_TYPES, string> = {
  [FIELD_TYPES.FIN_STATEMENTS]: '#80CC80',
  [FIELD_TYPES.CATEGORIES]: '#80AFD3',
  [FIELD_TYPES.GROUPS]: '#F7B380',
  [FIELD_TYPES.ACCOUNTS]: '#ECB4BB',
  [FIELD_TYPES.DRIVERS]: '#C6C6C6',
  [FIELD_TYPES.FORMULAS]: '#C557C8',
  [FIELD_TYPES.PREDICTIONS]: '#FFFFFF'
};

export const chartLabelFontSize = 8;
export const chartLabelFontWeight = 500;
export const chartLabelFontFamily = 'SageUI';

export const noDataColors = ['#EAECEF', '#D9DCE3', '#CCD6DB', '#ABABAB', '#668494'];
// export const defaultColors = ['#6FF3A3', '#C7384F', '#0073C1', '#FFB500', '#E96400', '#197B60', '#D86473'];
export const defaultColors = [
  '#6FF3A3',
  '#C7384F',
  '#0073C1',
  '#FFB500',
  '#E96400',
  '#197B60',
  '#D86473',
  '#88ff00',
  '#ff408c',
  '#00c2f2',
  '#ce36d9',
  '#664d4d',
  '#4D7080',
  '#ff0000',
  '#992673',
  '#4c0000',
  '#bf3030',
  '#070033',
  '#e6acac',
  '#3b264d',
  '#ffa280',
  '#36d9b8',
  '#30bf43',
  '#bf4d00',
  '#7f5940',
  '#5c00e6',
  '#e6c3ac',
  '#4c2900',
  '#b27700',
  '#f2e6b6',
  '#403d30',
  '#59163a',
  '#595300',
  '#aab32d',
  '#1f4d00',
  '#87a67c',
  '#602080',
  '#003322',
  '#567369',
  '#bffff2',
  '#23858c',
  '#994d61',
  '#002233',
  '#206080',
  '#003d73',
  '#ffcc00',
  '#3995e6',
  '#99a7cc',
  '#7979f2',
  '#1f0073',
  '#ff8800',
  '#716080',
  '#e6acda'
];

export const newDefaultRowStyles: Record<string, TableStyle> = {
  headerRow: {
    fontWeight: TABLE_FONT_WEIGHTS.BOLD,
    fontSize: 12,
    color: '#000000',
    fill: '#EBE7E7',
    fillPast: '#000000',
    fillVariance: '#EBE7E7',
    strokeColor: '#EBE7E7',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.BOTTOM
  },
  forecastsRow: {
    fontWeight: TABLE_FONT_WEIGHTS.BOLD,
    fontSize: 11,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 0.5,
    strokePosition: STROKE_BORDER_POSITIONS.BOTTOM
  },
  normalRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 10,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.NONE
  },
  categoriesRow: {
    fontWeight: TABLE_FONT_WEIGHTS.BOLDER,
    fontSize: 12,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.BOTTOM
  },
  groupsRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 11,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.NONE
  },
  accountsRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 10,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.NONE
  },
  assumptionsRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 10,
    color: '#197B60',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.NONE
  },
  formulasRow: {
    fontWeight: TABLE_FONT_WEIGHTS.BOLD,
    fontSize: 11,
    color: '#135D49',
    fill: '#EBE7E7',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 0.5,
    strokePosition: STROKE_BORDER_POSITIONS.BOTTOM
  },
  driverGroupsRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 11,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 0.5,
    strokePosition: STROKE_BORDER_POSITIONS.BOTTOM
  },
  driversRow: {
    fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
    fontSize: 10,
    color: '#000000',
    fill: '#FFFFFF',
    fillPast: '#B8B4B4',
    fillVariance: '#78E4C5',
    strokeColor: '#000000',
    strokeWidth: 1,
    strokePosition: STROKE_BORDER_POSITIONS.NONE
  }
};
